<template>
  <final-modal
    v-model="modalShow"
    @confirm="confirmOffset"
    @clear="clearOffset"
    @cancel="reset"
    :pageY="pageY"
  >
    <template v-slot:title
      >{{ $t("general.flight") }} {{ flightNumber }} ➤ {{ $t("general.hole") }}
      {{ holeNumber }}</template
    >
    <div class="accordion accordion-flush" id="holeSettingsAccordion">
      <div class="accordion-item" v-if="$root.can('offsets:manage')">
        <h2 class="accordion-header" id="holeSettingsOne">
          <button
            class="accordion-button"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#collapseOne"
            aria-expanded="true"
            aria-controls="collapseOne"
          >
            {{ $t("general.arrivalTime") }}
          </button>
        </h2>
        <div
          id="collapseOne"
          class="accordion-collapse collapse show"
          aria-labelledby="holeSettingsOne"
          data-bs-parent="#holeSettingsAccordion"
        >
          <div class="accordion-body p-0">
            <div class="container">
              <div class="row mt-2 mb-2">
                <div class="col-12 col-sm-6 text-center">
                  <p class="p-0 m-0">{{ $t("message.enterValueManually") }}:</p>
                  <vue-number-input
                    @update:model-value="onUpdateOffset"
                    @input="onInputOffset"
                    :model-value="offsetValue"
                    inline
                    center
                    controls
                    :min="-99"
                    :max="99"
                  ></vue-number-input>
                  <div class="d-flex justify-content-center">
                    <div class="mx-1">
                      <button
                        type="button"
                        class="btn btn-outline-success"
                        @click="confirmOffset"
                      >
                        {{ saveButtonText }}
                      </button>
                    </div>
                  </div>
                </div>
                <hr class="d-block d-sm-none m-0 mt-3 mb-2" />
                <div class="col-12 col-sm-6 text-center">
                  <div
                    class="pt-2 mb-2"
                    :style="{ lineHeight: '1rem', height: '40px' }"
                  >
                    {{ Math.abs(calculatedOffset) }}
                    {{
                      Math.abs(calculatedOffset) == 1
                        ? $t("general.minute")
                        : $t("general.minutes")
                    }}
                    {{
                      calculatedOffset == 0
                        ? `(${$t("general.onTime")})`
                        : calculatedOffset > 0
                        ? $t("general.late")
                        : $t("general.earlier")
                    }}<br /><small>{{
                      $t("message.enterValueAutomatically")
                    }}</small>
                  </div>

                  <button
                    type="button"
                    class="btn btn-outline-success"
                    @click="
                      offsetValue = calculatedOffset;
                      confirmOffset();
                    "
                  >
                    {{ $t("message.flightArrivedNow") }}
                  </button>
                </div>
                <hr
                  class="d-block d-sm-none m-0 mt-3 mb-2"
                  v-if="!isInitialOffset"
                />
                <div class="col-12 p-2 text-center">
                  <div class="mx-1" v-if="!isInitialOffset">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="clearOffset"
                    >
                      {{ $t("general.clear") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="accordion-item"
        v-if="$root.can('flightOvertakes:manage') && timeSheets?.length == 1"
      >
        <h2 class="accordion-header" id="holeSettingsTwo">
          <button
            class="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#collapseTwo"
            aria-expanded="false"
            aria-controls="collapseTwo"
          >
            {{ $t("general.flightOvertake") }}
          </button>
        </h2>
        <div
          id="collapseTwo"
          class="accordion-collapse collapse"
          aria-labelledby="holeSettingsTwo"
          data-bs-parent="#holeSettingsAccordion"
        >
          <div class="accordion-body p-0">
            <div class="p-2 text-center" v-if="flightOvertake == null">
              <template v-if="prevFlight?.id == null">
                <p class="mb-1">
                  {{ $t("message.flightIsFirstFlight") }}
                </p>
              </template>
              <template v-else>
                <p class="mb-1">
                  {{
                    $t("message.flightWillOvertakeFlightX", {
                      number: prevFlight?.number ?? "-",
                    })
                  }}
                </p>
                <button
                  type="button"
                  class="btn btn-outline-warning"
                  @click="confirmOvertake"
                >
                  {{
                    $t("general.confirm", {
                      item: $t("general.flightOvertake"),
                    })
                  }}
                </button>
              </template>
            </div>
            <div class="p-1 text-center" v-else>
              <p class="mb-1">
                {{
                  $t("message.flightOvertookFlightX", {
                    number: prevFlight?.number ?? "-",
                  })
                }}
              </p>
              <button
                type="button"
                class="btn btn-outline-danger"
                @click="undoOvertake"
              >
                {{ $t("general.undo", { item: $t("general.flightOvertake") }) }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="accordion-item" v-if="$root.can('refereeDecisions:manage')">
        <h2 class="accordion-header" id="holeSettingsThree">
          <button
            class="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#collapseThree"
            aria-expanded="false"
            aria-controls="collapseThree"
          >
            {{ $t("refereeDecisionsList.headline") }}
          </button>
        </h2>
        <div
          id="collapseThree"
          class="accordion-collapse collapse"
          aria-labelledby="holeSettingsThree"
          data-bs-parent="#holeSettingsAccordion"
        >
          <div class="accordion-body p-0">
            <ul class="list-group list-group-flush">
              <li
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                v-for="(
                  refereeDecision, refereeDecisionIndex
                ) in refereeDecisions"
                :key="refereeDecisionIndex"
                @click="
                  showRefereeDecisionModal(
                    timeSheetId,
                    flightId,
                    hole.number,
                    refereeDecision
                  )
                "
              >
                <div class="me-auto">
                  <div class="fw-bold">{{ refereeDecision.rule }}</div>
                  {{ refereeDecision.description }}
                </div>
                <span class="badge bg-danger rounded-pill">{{
                  refereeDecision.userName
                }}</span>
              </li>
              <li
                class="list-group-item d-flex justify-content-between align-items-start"
              >
                <div class="mx-auto">
                  <button
                    type="button"
                    class="btn btn-outline-danger"
                    @click="
                      showRefereeDecisionModal(
                        timeSheetId,
                        flightId,
                        hole.number
                      )
                    "
                  >
                    {{
                      $t("general.add", { item: $t("general.refereeDecision") })
                    }}
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </final-modal>
</template>

<script>
import FinalModal from "@/components/shared/final_modal.vue";
import { mapGetters } from "vuex";
import { http } from "@/http";
import { useToast } from "vue-toastification";
import { TOAST_CONFIGS } from "@/config/toasts";

export default {
  name: "SetOffsetModal",
  components: {
    FinalModal,
  },
  props: ["pageY"],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      modalShow: false,
      saveButtonText: "",
      timeSheetId: null,
      flightNumber: null,
      flightId: null,
      prevFlight: null,
      holeNumber: null,
      offsetValue: null,
      isInitialOffset: false,
      flightOvertake: null,
      hole: null,
      calculatedOffset: 0,
    };
  },
  computed: {
    ...mapGetters({
      timeSheets: "timeSheets/timeSheets",
      timeStamp: "timeSheets/getTimestamp",
    }),
    refereeDecisions() {
      return [].concat(
        ...this.timeSheets?.map(
          (timeSheet) =>
            timeSheet.refereeDecisions?.filter(
              (rD) =>
                rD.flightId == this.flightId &&
                rD.holeNumber == this.hole?.number
            ) ?? []
        )
      );
    },
  },
  methods: {
    calculateOffset() {
      if (this.hole == null) {
        this.calculatedOffset = 0;
      } else {
        const value = Math.floor(
          (Date.now() - this.hole.targetTimeMillis) / 60000
        );
        this.calculatedOffset = value <= 99 ? (value >= -99 ? value : -99) : 99;
      }
    },
    onInputOffset(v) {
      if (!isNaN(v.target.value)) {
        this.offsetValue = v.target.value * 1;
      }
    },
    handleOffsetError(err) {
      console.error(err);
      this.toast.error(
        this.$t("error.arrivalTimeOffset"),
        TOAST_CONFIGS.INVALID_REQUEST_ERROR
      );
    },
    handleOvertakeError(err) {
      console.error(err);
      this.toast.error(
        this.$t("error.flightOvertake"),
        TOAST_CONFIGS.INVALID_REQUEST_ERROR
      );
    },
    showRefereeDecisionModal(timeSheetId, flightId, holeNumber, data = null) {
      this.$parent.$refs.refereeDecisionModal.show(
        timeSheetId,
        flightId,
        holeNumber,
        data
      );
    },
    onUpdateOffset(value) {
      if (value != null) {
        value = value * 1;
      }
      if (value == 0 || value == null) {
        this.saveButtonText = this.$t("message.flightIsJustInTime");
      }
      if (value > 0) {
        this.saveButtonText = this.$t("message.flightIsNMinutesLate", {
          value,
        });
      }
      if (value < 0) {
        this.saveButtonText = this.$t("message.flightIsNMinutesEarlier", {
          value: Math.abs(value),
        });
      }
      this.offsetValue = value;
    },
    confirmOvertake() {
      http
        .post(`/api/v1/time-sheet/flight-overtake`, {
          timeSheetId: this.timeSheetId,
          targetFlightId: this.prevFlight.id,
          sourceFlightId: this.flightId,
          overtakingHoleNumber: this.holeNumber,
          userName: this.$root.userInfo().name,
          userInitials: this.$root.userInfo().initials,
        })
        .then(() => this.reset())
        .catch((err) => this.handleOvertakeError(err));
    },
    undoOvertake() {
      http
        .delete(`/api/v1/time-sheet/flight-overtake/${this.flightOvertake.id}`)
        .then(() => this.reset())
        .catch((err) => this.handleOvertakeError(err));
    },
    confirmOffset() {
      this.offsetValue = this.offsetValue ?? 0;
      this.postOffset();
    },
    clearOffset() {
      this.offsetValue = null;
      this.postOffset();
    },
    reset() {
      this.saveButtonText = this.$t("message.flightIsJustInTime");
      this.timeSheetId = null;
      this.flightNumber = null;
      this.flightId = null;
      this.prevFlight = null;
      this.holeNumber = null;
      this.offsetValue = null;
      this.modalShow = false;
      this.isInitialOffset = false;
      this.flightOvertake = null;
      this.hole = null;
      this.calculatedOffset = 0;
    },
    postOffset() {
      http
        .post(`/api/v1/time-sheet/offset`, {
          timeSheetId: this.timeSheetId,
          flightId: this.flightId,
          holeNumber: this.holeNumber,
          value: this.offsetValue,
          userName: this.$root.userInfo().name,
          userInitials: this.$root.userInfo().initials,
        })
        .then(() => this.reset())
        .catch((err) => this.handleOffsetError(err));
    },
    show(data) {
      this.timeSheetId = data.timeSheetId;
      this.flightNumber = data.flightNumber * 1;
      this.flightId = data.flightId;
      this.prevFlight = data.prevFlight;
      this.holeNumber = data.holeNumber * 1;
      this.isInitialOffset = data.offset == null;
      this.onUpdateOffset(data.offset?.value ?? 0);
      this.modalShow = true;
      this.flightOvertake = data.flightOvertake;
      this.hole = data.hole;
      if (this.hole) {
        this.calculateOffset();
      }
    },
  },
  watch: {
    timeStamp() {
      this.calculateOffset();
    },
  },
};
</script>

<style lang="scss">
.list-group-item.list-group-item-action {
  &:hover {
    cursor: pointer;
  }
}

/* .accordion {
  .accordion-item {
    border: none;
    border-radius: 0 !important;
  }
} */
</style>
